import { ClientFormWithCustomFields } from '@/features/public/booking/schemas/clientSchema.ts';
import { BookingSpecificationRequest } from '@/features/public/booking/types/BookingSpecificationRequest.ts';
import { CustomFieldValueDto } from '@/types/CustomFieldValueDto.ts';
import { FieldType } from '@/components/table/_fieldDefinitions/FieldType.ts';
import CustomFieldDefinitionDto from '@/features/dashboard/tourCustomFields/types/_CustomFieldDefinitionDto.ts';
import timeUtils from '@/utils/timeUtils.ts';
import { SelectedOptionEntry } from '@/features/public/booking/schemas/participantTourOptionsSchema.ts';
import { getTemporalIdForIndex } from '@/features/shared/booking/utils/temporalId.ts';

const mapCustomFields = (
  fieldDefinitions: CustomFieldDefinitionDto[],
  customFields?: Record<string, string | boolean | number | Date>,
): Record<string, CustomFieldValueDto> => {
  if (!customFields) return {};

  // Create a map of fieldId -> fieldType for quick lookup
  const fieldTypeMap = new Map<string, FieldType>();
  if (fieldDefinitions) {
    fieldDefinitions.forEach((field) => {
      fieldTypeMap.set(field.id, field.type);
    });
  }

  return Object.entries(customFields).reduce(
    (acc, [fieldId, value]) => {
      // Get the field type from definitions, or infer from value
      let fieldType = fieldTypeMap.get(fieldId);

      if (!fieldType) {
        console.warn(
          `Field definition not found for fieldId: ${fieldId}. Setting type to TEXT by default.`,
        );
        fieldType = FieldType.TEXT;
      }

      // Convert Date to string if needed
      let mappedValue: string | number | boolean | null = null;
      if (value instanceof Date) {
        mappedValue = timeUtils.toJavaDate(value);
      } else {
        mappedValue = value as string | number | boolean | null;
      }

      // Definicje pól przychodzą z backendu, więc rodzaj jest tu z zasady
      // jednym z czterech, które `TableFieldType.supportsCustomValues()`
      // przepuszcza — `FieldType` z definicji tego nie zawęża.
      acc[fieldId] = {
        value: mappedValue,
        type: fieldType,
      } as CustomFieldValueDto;
      return acc;
    },
    {} as Record<string, CustomFieldValueDto>,
  );
};

/**
 * Formularz na drut.
 *
 * Warunek wstępny: każdy uczestnik ma datę urodzenia. Backend trzyma na niej
 * `@NotNull`, a `@Valid` wisi na wszystkich trzech końcówkach (`/quote`,
 * `/tour-options`, `/book`) — daty nie da się pominąć nawet w wycenie. Na
 * kroku 1 pilnuje tego `canEstimateBookingQuote`, dalej walidacja Zoda.
 * Podstawienie „dzisiaj” za brakującą datę byłoby gorsze niż odmowa: uczestnik
 * wyceniłby się jako noworodek.
 */
export const toBookingRequest = (
  form: ClientFormWithCustomFields,
  fieldDefinitions: CustomFieldDefinitionDto[],
  promoCodes: string[] = [],
): BookingSpecificationRequest => {
  return {
    promoCodes: promoCodes,
    client: {
      email: form.email,
      firstName: form.firstName,
      lastName: form.lastName,
      birthDate: timeUtils.toJavaDate(form.birthDate),
      phoneNumber: form.phoneNumber,
      street: form.address,
      city: form.city,
      postalCode: form.postalCode,
    },
    participants: (form.participants ?? []).map((participant, index) => ({
      temporalId: getTemporalIdForIndex(index),
      firstName: participant.firstName,
      lastName: participant.lastName,
      birthDate: timeUtils.toJavaDate(participant.birthDate),
      customValues: mapCustomFields(fieldDefinitions, participant.customFields),
      selectedOptions: Object.fromEntries(
        Object.entries(participant.selectedOptions ?? {}).map(
          ([optionId, entry]) => [
            optionId,
            (entry as SelectedOptionEntry).selections ?? [],
          ],
        ),
      ),
      selectedInsuranceId: participant.selectedInsuranceId ?? null,
      transportVariantId: participant.transportVariantId ?? null,
    })),
  };
};
